import React, { useState } from "react";
import MeasurementResultsTable from "./MeasurementResultsTable";
import { formatHrDateTime, formatNum } from "../flourQualityUtils";

type MeasurementRow = Record<string, unknown> & {
  id: number;
  measured_at?: string | null;
  is_valid?: boolean | null;
  sample_weight?: number | null;
  method?: string | null;
  operator?: string | null;
  device_sn?: string | null;
  sample_name?: string | null;
  source_file?: string | null;
  evaluation?: string | null;
  zboogie_farinograph_results?: unknown[];
  zboogie_extensograph_results?: unknown[];
  zboogie_amylograph_results?: unknown[];
};

type Props = {
  device: "farinograph" | "extensograph" | "amylograph";
  label: string;
  measurements: MeasurementRow[];
};

const resultsKey: Record<Props["device"], string> = {
  farinograph: "zboogie_farinograph_results",
  extensograph: "zboogie_extensograph_results",
  amylograph: "zboogie_amylograph_results",
};

const DeviceMeasurementsPanel = ({ device, label, measurements }: Props) => {
  const [openId, setOpenId] = useState<number | null>(measurements[0]?.id ?? null);

  if (!measurements.length) {
    return (
      <div className="fq-device-panel fq-device-empty">
        <h4>{label}</h4>
        <p className="mes-hint">Još nema uvezenih mjerenja za ovaj blend.</p>
      </div>
    );
  }

  return (
    <div className="fq-device-panel">
      <h4>{label} <span className="fq-count">({measurements.length})</span></h4>
      <div className="fq-measurement-list">
        {measurements.map((m) => {
          const results = (m[resultsKey[device]] as unknown[]) || [];
          const isOpen = openId === m.id;
          return (
            <div key={m.id} className={`fq-measurement-card ${isOpen ? "open" : ""}`}>
              <button
                type="button"
                className="fq-measurement-card-head"
                onClick={() => setOpenId(isOpen ? null : m.id)}
              >
                <div className="fq-measurement-card-meta">
                  <strong>#{m.id}</strong>
                  <span>{formatHrDateTime(m.measured_at as string)}</span>
                  {m.is_valid === true && <span className="badge stock-good">valjano</span>}
                  {m.is_valid === false && <span className="badge badge-danger">nevaljano</span>}
                  {m.is_valid == null && <span className="badge badge-muted">n/p</span>}
                </div>
                <div className="fq-measurement-card-sub">
                  {m.sample_name && <span>Uzorak: {m.sample_name}</span>}
                  {m.sample_weight != null && <span>Masa: {formatNum(m.sample_weight)} g</span>}
                  <span>{results.length} točaka</span>
                </div>
              </button>
              {isOpen && (
                <div className="fq-measurement-card-body">
                  <dl className="fq-meta-grid">
                    <div><dt>Metoda</dt><dd>{m.method || "—"}</dd></div>
                    <div><dt>Operator</dt><dd>{m.operator || "—"}</dd></div>
                    <div><dt>Uređaj SN</dt><dd>{m.device_sn || "—"}</dd></div>
                    <div><dt>Datoteka</dt><dd>{m.source_file || "—"}</dd></div>
                    {device === "farinograph" && (
                      <div><dt>Ocjena</dt><dd>{m.evaluation || "—"}</dd></div>
                    )}
                  </dl>
                  <MeasurementResultsTable
                    device={device}
                    results={results as never[]}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DeviceMeasurementsPanel;
